import { createContext, useCallback, useContext, useMemo, useState } from "react";

type AuthContextValue = {
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  setTokens: (access: string, refresh: string) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function getStoredToken(key: string): string | null {
  // Check cookie first
  const match = document.cookie.match(new RegExp(`${key}=([^;]+)`));
  if (match?.[1]) return match[1];
  // Fallback to localStorage
  return localStorage.getItem(key);
}

function storeToken(key: string, value: string) {
  document.cookie = `${key}=${value}; path=/; max-age=604800; samesite=lax`;
  localStorage.setItem(key, value);
}

function clearToken(key: string) {
  document.cookie = `${key}=; path=/; max-age=0`;
  localStorage.removeItem(key);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [accessToken, setAccessToken] = useState<string | null>(() =>
    getStoredToken("access_token"),
  );
  const [refreshToken, setRefreshToken] = useState<string | null>(() =>
    getStoredToken("refresh_token"),
  );

  const setTokens = useCallback((access: string, refresh: string) => {
    storeToken("access_token", access);
    storeToken("refresh_token", refresh);
    setAccessToken(access);
    setRefreshToken(refresh);
  }, []);

  const logout = useCallback(() => {
    clearToken("access_token");
    clearToken("refresh_token");
    setAccessToken(null);
    setRefreshToken(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      accessToken,
      refreshToken,
      isAuthenticated: !!accessToken,
      setTokens,
      logout,
    }),
    [accessToken, refreshToken, setTokens, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
